import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { getPermissions, Permission, PermissionName, Role } from '@src/app/model/userdata';

/**
 * Checks permissions of the user currently signed in
 * The code below was inspired by the backend's AuthActionBuilders
 * > Use the SecureActionDirective to hide/show components based on permissions in a view
 */
@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  public $currentPermissions: BehaviorSubject<Permission[]> = new BehaviorSubject([]);
  public currentPermissions: Permission[] = [];

  constructor() {
  }

  /**
   * Bit-matches the required level to the permission bit. For example
   * 6 & 5 == 5 because 0111 & 0101 = 0101 == 5
   * 6 & 2 == 2 because 0110 & 0010 = 0010 == 2
   * 6 & 1 != 1 because 0110 & 0001 = 0000 == 0
   */
  public static bitMatch(bit: number, required: number): boolean {
    return (bit & required) === required;
  }

  public setPermissions(roles: Role[]): void {
    const newPermissions = getPermissions(roles);
    this.$currentPermissions.next(newPermissions);
    this.currentPermissions = newPermissions;
  }

  /**
   * Observe permission, can be useful when displaying things.
   * Preferred way to retrieve permissions, will always use up-to-date data!
   */
  public observePermission$(operationName: string, requiredPermission: PermissionName): Observable<boolean> {
    return this.$currentPermissions.pipe(
      map(permissions => this.isPermitted(permissions, operationName, requiredPermission))
    );
  }

  /**
   * Get instant permissions (sync). Useful for one-time actions.
   * WARNING: will likely return unexpected result in on-load scenarios due to sync nature!
   * As such, observePermission fn is the preferred way to get permissions.
   */
  public getPermission(operationName: string, requiredPermission: PermissionName): boolean {
    const permitted = this.isPermitted(this.currentPermissions, operationName, requiredPermission);
    if (!permitted) {
      // this.modalService.showWarning({translatePrefix: 'permissions.restricted'});
    }
    return permitted;
  }

  public isAnyPermitted(
    permissions: Permission[],
    operationNames: string[],
    requiredPermission: PermissionName
  ): boolean {
    return operationNames.some(operationName => this.isPermitted(permissions, operationName, requiredPermission));
  }

  /**
   * Compares the user's permission with the required permission
   * Special case operation star skips the operation check and only checks the permission level
   */
  public isPermitted(
    permissions: Permission[],
    operationName: string,
    requiredPermission: PermissionName
  ): boolean {
    return permissions
      .filter((p) => {
        if (p.name === '*') {
          return true;
        } else {
          return p.name === operationName;
        }
      })
      .some(p => PermissionsService.bitMatch(p.bit, requiredPermission));
  }
}
